import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, ArrowRightIcon, Globe } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ProvenanceTemplate, VideoTutorialBox } from "./-components";
import type {
  PlagiarismSource,
  ProvenanceV2EntryBase,
} from "@/lib/types/provenance";
import type { CarouselApi } from "@/components/ui/carousel";

import { plagiarismDetection } from "@/lib/textAnalyze";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/_provenance/copyright-detector")({
  component: RouteComponent,
  staticData: {
    title: "Copyright Detection",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Scan for copyrighted text in documents and web content with Polygraf AI’s Copyright Detector - ensure licensing compliance, avoid infringement, and deploy locally or on-premises.",
      },
    ],
  }),
});

function RouteComponent() {
  const { title } = Route.useMatch({
    select: (m) => m.staticData,
  });
  return (
    <div className="relative">
      <VideoTutorialBox
        title="New to Polygraf?"
        description={`Learn how to use \n Copyright Detector`}
        youtubeUrl="https://youtu.be/KkxjwBcItxQ"
      />
      <ProvenanceTemplate
        title={title!}
        subtitle="Identify copyrighted content and sources."
        model="PLAGIARISM"
        analyze={(text) => plagiarismDetection(text)}
        SuccessSection={CopyrightDetectResult}
        nextPage={{
          title: "Writing Analysis",
          linkOptions: {
            to: "/writing-analysis",
          },
        }}
        __addHumanizeButton
      />
    </div>
  );
}

function CopyrightDetectResult({
  entry: { result },
}: {
  entry: ProvenanceV2EntryBase<"PLAGIARISM", string>;
}) {
  return (
    <>
      <div className="mt-4 mb-4 h-[2px] bg-gray-200 dark:bg-gray-800" />
      <div className="space-y-4">
        {Object.entries(result.category_averages)
          .filter(([, percentage]) => percentage > 3)
          .map(([name, percentage]) => (
            <Card
              className="flex flex-row items-center gap-4 bg-[#FAFAFA] p-4 dark:bg-transparent"
              key={name}
            >
              <div className="flex items-center justify-items-start gap-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                  <Globe className="size-5 text-gray-600" />
                </div>
                <div>
                  <div className="text-lg font-medium">{name}</div>
                  <div className="text-xl font-semibold">
                    {percentage.toFixed(2)}%
                  </div>
                </div>
              </div>
            </Card>
          ))}

        <div>
          {result.sources.length > 0 ? (
            <SourcesCarousel sources={result.sources} />
          ) : (
            <span>
              Your text appears to be original, with no matches to existing
              sources or copyrighted material. Great work!
            </span>
          )}
        </div>
      </div>
    </>
  );
}

function SourcesCarousel({ sources }: { sources: Array<PlagiarismSource> }) {
  const [api, setApi] = useState<CarouselApi>();

  const [scrollState, setScrollState] = useState<{
    page: number;
    canScrollPrevious: boolean;
    canScrollNext: boolean;
  }>({
    page: 0,
    canScrollPrevious: false,
    canScrollNext: false,
  });

  useEffect(() => {
    if (!api) return;

    const cb = () =>
      setScrollState({
        page: api.selectedScrollSnap(),
        canScrollNext: api.canScrollNext(),
        canScrollPrevious: api.canScrollPrev(),
      });

    api.on("select", cb);
    cb();

    return () => void api.off("select", cb);
  }, [api]);

  const scrollNext = useCallback(() => api?.scrollNext(), [api]);
  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);

  return (
    <Carousel setApi={setApi}>
      <div className="flex flex-col">
        <Card className="p-4">
          <CarouselContent>
            {sources.map(
              ({ idx, matched_content, score, url, url_category }) => (
                <CarouselItem className="flex-col" key={idx}>
                  <Badge>{idx}</Badge>
                  <div className="flex-row">
                    <Badge variant={"outline"}>{url_category}</Badge>
                    <Badge variant={"outline"}>
                      Matching: {score.toFixed(2)}%
                    </Badge>
                  </div>
                  <a
                    href={url}
                    className="block overflow-hidden font-bold overflow-ellipsis underline"
                  >
                    {url.length > 50 ? url.substring(0, 50) : url}
                  </a>
                  <span className="block overflow-hidden overflow-ellipsis">
                    {matched_content.length > 200
                      ? matched_content.substring(0, 200) + "..."
                      : matched_content}
                  </span>
                </CarouselItem>
              ),
            )}
          </CarouselContent>
        </Card>
        <div className="flex w-full flex-row justify-between py-2">
          <Button
            size="icon"
            variant="outline"
            className={cn(!scrollState.canScrollPrevious && "invisible")}
            disabled={!scrollState.canScrollPrevious}
            onClick={scrollPrev}
          >
            <ArrowLeftIcon />
          </Button>
          {scrollState.page + 1} of {sources.length}
          <Button
            size="icon"
            variant="outline"
            className={cn(!scrollState.canScrollNext && "invisible")}
            disabled={!scrollState.canScrollNext}
            onClick={scrollNext}
          >
            <ArrowRightIcon />
          </Button>
        </div>
      </div>
    </Carousel>
  );
}
